<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration, withAlpha } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 600));
const accent = computed(() => props.block.accentColor || "#22d3ee");

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "radial-gradient(circle at center, #052238 0%, #020a14 75%)",
      fontFamily: props.block.fontFamily || "'Orbitron', 'Exo 2', sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-accent": accent.value,
      "--cd-glow": withAlpha(accent.value, 0.55),
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.6}px`,
    height: `${base * 2.6}px`,
    fontSize: `${base}px`,
  };
});
</script>

<template>
  <div class="cd-holo" :style="containerStyle">
    <div class="cd-holo__grid" aria-hidden="true" />
    <div class="cd-holo__scan" aria-hidden="true" />

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-holo__cell"
      :style="{ animationDelay: `${i * 130}ms` }"
    >
      <div
        class="cd-holo__hud"
        :style="{ width: cardSize.width, height: cardSize.height }"
      >
        <svg class="cd-holo__ring" viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r="48" />
          <circle class="cd-holo__ring-arc" cx="50" cy="50" r="42" />
          <circle
            class="cd-holo__ring-arc cd-holo__ring-arc--rev"
            cx="50"
            cy="50"
            r="36"
          />
        </svg>
        <div class="cd-holo__cross-h" />
        <div class="cd-holo__cross-v" />
        <span
          class="cd-holo__digit"
          :key="seg.value"
          :style="{
            fontSize: cardSize.fontSize,
            color: block.digitColor || accent,
          }"
        >
          {{ seg.value }}
        </span>
      </div>
      <div
        class="cd-holo__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || withAlpha(accent, 0.75),
        }"
      >
        {{ seg.label }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-holo {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
  isolation: isolate;
  color: var(--cd-accent);
}
.cd-holo__grid {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(var(--cd-glow) 1px, transparent 1px) 0 0 / 32px 32px,
    linear-gradient(90deg, var(--cd-glow) 1px, transparent 1px) 0 0 / 32px 32px;
  opacity: 0.18;
  mask-image: radial-gradient(circle at center, #000 35%, transparent 80%);
  pointer-events: none;
}
.cd-holo__scan {
  position: absolute;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(180deg, transparent, var(--cd-glow), transparent);
  opacity: 0.4;
  pointer-events: none;
  animation: cd-holo-scan 5s linear infinite;
}
@keyframes cd-holo-scan {
  0% {
    top: -10%;
  }
  100% {
    top: 110%;
  }
}
.cd-holo__cell {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  animation: cd-holo-pop 700ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
@keyframes cd-holo-pop {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.cd-holo__hud {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cd-holo__ring {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  fill: none;
  stroke: var(--cd-accent);
  filter: drop-shadow(0 0 6px var(--cd-glow));
}
.cd-holo__ring circle {
  stroke-width: 1;
  opacity: 0.5;
}
.cd-holo__ring-arc {
  stroke-width: 2;
  stroke-dasharray: 80 220;
  transform-origin: 50% 50%;
  animation: cd-holo-spin 6s linear infinite;
  opacity: 0.9;
}
.cd-holo__ring-arc--rev {
  animation-direction: reverse;
  animation-duration: 9s;
  stroke-dasharray: 40 200;
}
@keyframes cd-holo-spin {
  to {
    transform: rotate(360deg);
  }
}
.cd-holo__cross-h,
.cd-holo__cross-v {
  position: absolute;
  background: var(--cd-glow);
  pointer-events: none;
}
.cd-holo__cross-h {
  left: 12%;
  right: 12%;
  height: 1px;
  top: 50%;
  opacity: 0.4;
}
.cd-holo__cross-v {
  top: 12%;
  bottom: 12%;
  width: 1px;
  left: 50%;
  opacity: 0.4;
}
.cd-holo__digit {
  position: relative;
  z-index: 2;
  font-weight: 700;
  letter-spacing: 0.05em;
  font-variant-numeric: tabular-nums;
  text-shadow:
    0 0 8px var(--cd-glow),
    0 0 22px var(--cd-glow);
  display: inline-block;
  animation: cd-holo-glitch var(--cd-tick, 600ms) ease-out;
}
@keyframes cd-holo-glitch {
  0% {
    opacity: 0;
    transform: translateY(-6px);
    filter: blur(2px);
  }
  60% {
    transform: translateY(2px);
    filter: blur(0);
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    filter: blur(0);
  }
}
.cd-holo__label {
  text-transform: uppercase;
  letter-spacing: 0.25em;
  font-weight: 500;
}
</style>
